import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Network,
  Plus,
  RefreshCw,
  Send,
  Server,
  Settings,
  Shield,
  Timer,
  Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/NumberInput";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/uiext";
import { TwoFactorCard } from "@/components/TwoFactorCard";
import { LegalEditorCard } from "@/components/LegalEditorCard";
import { toast } from "sonner";

/**
 * Phase 4m — Factory Reset card.
 *
 * The NUCLEAR option. Wipes every collection in the Elswitch DB so the
 * operator can rebuild from scratch. Triple-gated:
 *   1. Current admin password
 *   2. Admin email (must match admin_notify_email on file)
 *   3. Typed phrase: "WIPE EVERYTHING"
 *
 * Survivors after wipe: the platform admin login is re-seeded
 * automatically so the operator can log straight back in. Everything
 * else is gone.
 *
 * Lives inside a visible-at-all-times "Danger zone" card on Platform
 * → Settings. Clicking "Factory reset…" expands an inline confirm
 * panel in the same card (not a modal — harder to accidentally
 * dismiss and more honest about what's about to happen).
 */
function FactoryResetCard() {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ password: "", email: "", phrase: "" });
  const [busy, setBusy] = useState(false);

  const allValid =
    !!form.password &&
    !!form.email &&
    form.phrase.trim() === "WIPE EVERYTHING";

  const run = async () => {
    if (!allValid) return;
    // Double-prompt because this is actually irreversible.
    if (!window.confirm(
      "LAST CHANCE.\n\nThis will delete every tenant, every CDR, every platform setting (Resend key, TLS config, billing, security settings), and every audit log.\n\nYou will be logged out and will need to re-enter the admin password on a fresh database.\n\nType OK to the next prompt to proceed."
    )) return;
    if (!window.confirm("Final confirmation — factory reset now?")) return;
    setBusy(true);
    try {
      await api.post("/platform/factory-reset", {
        password: form.password,
        admin_email: form.email,
        confirm_phrase: form.phrase,
      });
      toast.success(
        "Database wiped. Redirecting to login…",
        { duration: 4000 }
      );
      setTimeout(() => {
        localStorage.removeItem("token");
        window.location.href = "/login";
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Factory reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/2"
      data-testid="card-factory-reset"
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-[hsl(var(--danger))]/10 p-2 mt-0.5">
              <AlertTriangle className="h-5 w-5 text-[hsl(var(--danger))]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[hsl(var(--danger))]">
                Danger zone — Factory reset
              </h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-prose">
                Permanently delete <b>every</b> tenant, CDR, platform
                setting (including your Resend key, TLS certs, Stripe
                config and billing), every audit log and every security
                event. The platform admin login will be re-seeded so you
                can log back in immediately on a clean database.
                <br />
                <span className="text-[hsl(var(--danger))] font-semibold">
                  There is no undo.
                </span>
              </p>
            </div>
          </div>
          {!expanded && (
            <Button
              variant="outline"
              className="border-[hsl(var(--danger))]/50 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10"
              onClick={() => setExpanded(true)}
              data-testid="factory-reset-open-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Factory reset…
            </Button>
          )}
        </div>

        {expanded && (
          <div
            className="rounded-md border border-[hsl(var(--danger))]/30 bg-background p-4 space-y-3"
            data-testid="factory-reset-panel"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--danger))]">
              Triple-factor confirmation
            </div>
            <div>
              <Label className="text-xs">1. Your admin password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="factory-reset-password"
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label className="text-xs">
                2. Admin email (must match the one in{" "}
                <span className="font-mono">admin_notify_email</span>)
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="factory-reset-email"
                placeholder="admin@your.domain"
              />
            </div>
            <div>
              <Label className="text-xs">
                3. Type{" "}
                <span className="font-mono font-bold text-[hsl(var(--danger))]">
                  WIPE EVERYTHING
                </span>{" "}
                to confirm:
              </Label>
              <Input
                value={form.phrase}
                onChange={(e) => setForm({ ...form, phrase: e.target.value })}
                data-testid="factory-reset-phrase"
                placeholder="WIPE EVERYTHING"
                className={
                  form.phrase && form.phrase.trim() !== "WIPE EVERYTHING"
                    ? "border-[hsl(var(--danger))]/50 font-mono"
                    : "font-mono"
                }
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setExpanded(false);
                  setForm({ password: "", email: "", phrase: "" });
                }}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                className="bg-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/90 text-white"
                onClick={run}
                disabled={!allValid || busy}
                data-testid="factory-reset-confirm-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {busy ? "Wiping…" : "Wipe the entire database"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The catalogue of engine modules the platform admin can flip on/off.
 *  - `available: true`  → the toggle is live and the engine respects it.
 *  - `available: false` → "Coming soon" badge, switch is disabled. Used to
 *    surface the public roadmap so customers see what's coming and the
 *    flag scaffolding is in place when implementation lands.
 *
 * Keep this in sync with the backend whitelist in `update_platform_settings`.
 */
const ENGINE_MODULES_CATALOG = [
  {
    key: "failover_routing",
    label: "Failover routing",
    desc: "When the primary trunk on a route is unreachable, automatically try the configured failover trunks in order.",
    available: true,
  },
  {
    key: "dialplan_transforms",
    label: "Dial-plan transformations",
    desc: "Apply per-route prefix strip / prepend on outbound calls (e.g. 9 for outside line, +1 for E.164).",
    available: true,
  },
  {
    key: "trunk_options_probe",
    label: "Trunk reachability monitor",
    desc: "Send periodic SIP OPTIONS probes to every trunk and surface a reachability indicator on the Trunks page.",
    available: true,
  },
  {
    key: "coturn_sidecar",
    label: "STUN / TURN sidecar (coturn)",
    desc: "Self-hosted STUN/TURN for WebRTC bridging and aggressive NAT traversal.",
    available: false,
  },
];

function EngineModulesCard({ modules, onChange, onSave }) {
  return (
    <Card className="max-w-2xl" data-testid="card-engine-modules">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2 mt-0.5">
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Engine modules</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Optional capabilities the SIP engine can run. Flip them on as you need them.
              Items marked <b>Coming soon</b> are part of the roadmap — the toggle will
              activate once the implementation ships in a future upgrade.
            </p>
          </div>
        </div>
        <ul className="divide-y divide-border rounded-md border border-border">
          {ENGINE_MODULES_CATALOG.map((m) => {
            const checked = !!modules[m.key];
            return (
              <li
                key={m.key}
                className="flex items-start justify-between gap-3 px-3 py-3"
                data-testid={`engine-module-${m.key}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{m.label}</span>
                    {m.available ? (
                      <Badge
                        variant="outline"
                        className="gap-1 border-emerald-500/40 text-emerald-500 text-[10px] px-1.5 py-0"
                      >
                        <CheckCircle2 className="h-2.5 w-2.5" /> Available
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="gap-1 border-amber-500/40 text-amber-500 text-[10px] px-1.5 py-0"
                      >
                        <Clock className="h-2.5 w-2.5" /> Coming soon
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                    {m.desc}
                  </p>
                </div>
                <Switch
                  checked={checked && m.available}
                  disabled={!m.available}
                  onCheckedChange={(v) => onChange({ [m.key]: v })}
                  data-testid={`toggle-${m.key}`}
                />
              </li>
            );
          })}
        </ul>
        <div className="pt-1">
          <Button onClick={onSave} data-testid="engine-modules-save">
            Save module flags
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status, enabled }) {
  if (!enabled) {
    return (
      <Badge variant="outline" className="gap-1" data-testid="lp-status-disabled">
        <Clock className="h-3 w-3" /> Disabled
      </Badge>
    );
  }
  if (status === "bound") {
    return (
      <Badge variant="outline"
             className="gap-1 border-emerald-500/40 text-emerald-500"
             data-testid="lp-status-bound">
        <CheckCircle2 className="h-3 w-3" /> Bound
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="outline"
             className="gap-1 border-destructive/50 text-destructive"
             data-testid="lp-status-error">
        <AlertTriangle className="h-3 w-3" /> Bind error
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1" data-testid="lp-status-pending">
      <Activity className="h-3 w-3 animate-pulse" /> Binding…
    </Badge>
  );
}

function MaxCallDurationCard({ value, onChange, onSave }) {
  const v = Number(value || 0);
  const human = v <= 0
    ? "Unlimited (no platform-wide cap)"
    : `${Math.floor(v / 3600)}h ${Math.floor((v % 3600) / 60)}m ${v % 60}s`;
  return (
    <Card className="max-w-2xl" data-testid="card-max-call-duration">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2 mt-0.5">
            <Timer className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Maximum call duration</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-prose">
              Hard ceiling, in seconds, after which the engine force-tears
              down a call. Closes the audit gap where a lost BYE could leave
              a "ghost call" reserving channels forever. Customers may set
              their own lower limit per universe; if they don't, this default
              applies. Use <b>0</b> for unlimited (not recommended on PSTN).
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-[160px_1fr] gap-3 items-end">
          <div>
            <Label className="text-xs">Seconds (0 – 86400)</Label>
            <NumberInput
              min={0}
              max={86400}
              fallback={21600}
              value={value ?? 0}
              onChange={(n) => onChange(n)}
              data-testid="input-max-call-duration"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">{human}</span>
          </div>
        </div>
        <Button onClick={onSave} data-testid="btn-save-max-call-duration">
          Save platform default
        </Button>
      </CardContent>
    </Card>
  );
}

function TlsListenerCard({ s, onChange, onSave }) {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const reload = useCallback(async () => {
    try {
      const r = await api.get("/platform/tls/status");
      setStatus(r.data);
    } catch {
      /* silent */
    }
  }, []);
  useEffect(() => {
    reload();
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, [reload]);

  const cert = status?.cert || {};
  const ready = !!status?.ready_to_enable;
  const listener = status?.listener || {};

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/platform/tls/test");
      if (r.data?.ok) {
        toast.success(`TLS handshake OK (${r.data.cipher})`);
      } else {
        toast.error(`TLS test failed at ${r.data.stage}: ${r.data.error}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "TLS test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="max-w-2xl" data-testid="card-tls-listener">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2 mt-0.5">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-base font-semibold">SIP-TLS listener</h3>
              <div className="flex items-center gap-2">
                {listener.reported_active ? (
                  <Badge className="gap-1" data-testid="tls-state-active">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </Badge>
                ) : s.tls_enabled ? (
                  <Badge variant="outline" className="gap-1" data-testid="tls-state-pending">
                    <Activity className="h-3 w-3 animate-pulse" /> Starting…
                  </Badge>
                ) : (
                  <Badge variant="outline" data-testid="tls-state-off">
                    Disabled
                  </Badge>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-prose">
              Encrypts SIP signalling between the engine and TLS-capable
              peers (RFC 3261 §26). Certs are sourced from Caddy's
              auto-managed Let's Encrypt store and rotate hands-free. Toggle
              this on once your DNS resolves a NAPTR/SRV pointing here.
            </p>
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border bg-muted/30 p-3">
          <div>
            <Label>Enable TLS</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Off until cert is ready. We never bind 5061 without a usable cert.
            </p>
          </div>
          <Switch
            checked={!!s.tls_enabled}
            onCheckedChange={(v) => onChange({ tls_enabled: v })}
            disabled={!ready && !s.tls_enabled}
            data-testid="toggle-tls-enabled"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">TLS port</Label>
            <NumberInput
              min={1}
              max={65535}
              fallback={5061}
              value={s.tls_port ?? 5061}
              onChange={(n) => onChange({ tls_port: n })}
              data-testid="input-tls-port"
            />
          </div>
          <div>
            <Label className="text-xs">Cert domain (FQDN)</Label>
            <Input
              type="text"
              value={s.tls_cert_domain || ""}
              onChange={(e) => onChange({ tls_cert_domain: e.target.value.trim() })}
              placeholder="e.g. sip.yourdomain.com"
              data-testid="input-tls-cert-domain"
            />
          </div>
          <div>
            <Label className="text-xs">Cert source</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm mt-1"
              value={s.tls_cert_source || "caddy"}
              onChange={(e) => onChange({ tls_cert_source: e.target.value })}
              data-testid="select-tls-cert-source"
            >
              <option value="caddy">Caddy (auto Let&apos;s Encrypt)</option>
              <option value="manual">Manual PEM at /opt/elswitch/shared/tls/</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Readiness</Label>
            <div className="text-sm mt-2 font-mono break-words" data-testid="tls-readiness">
              {ready ? (
                <span className="text-green-600">cert OK</span>
              ) : (
                <span className="text-destructive">
                  {cert.reason || "not ready"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Phase N+ — surface the rich Caddy probe diagnostic so the operator
            can see exactly which path was inspected. */}
        {!ready && cert.diagnostic && (cert.diagnostic.tried?.length > 0) && (
          <details className="rounded-md border bg-muted/30 p-3 text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Show diagnostic ({cert.diagnostic.errors?.length || 0} issue
              {cert.diagnostic.errors?.length === 1 ? "" : "s"})
            </summary>
            <div className="mt-2 space-y-2">
              {cert.diagnostic.errors?.length > 0 && (
                <div>
                  <div className="text-muted-foreground mb-1">Errors:</div>
                  <ul className="space-y-1 list-disc pl-5">
                    {cert.diagnostic.errors.map((err, i) => (
                      <li key={i} className="font-mono break-all">{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="text-muted-foreground mb-1">Paths inspected:</div>
                <ul className="space-y-0.5 list-disc pl-5">
                  {cert.diagnostic.tried.map((p, i) => (
                    <li key={i} className="font-mono break-all">{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          </details>
        )}

        {cert.metadata?.ok && (
          <div className="rounded-md border bg-card p-3 text-xs space-y-1" data-testid="tls-cert-meta">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Issuer</span>
              <span className="font-mono break-all">{cert.metadata.issuer}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Not before</span>
              <span className="font-mono">{cert.metadata.not_before}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Not after</span>
              <span className="font-mono">{cert.metadata.not_after}</span>
            </div>
            {cert.metadata.sans?.length > 0 && (
              <div>
                <span className="text-muted-foreground">SANs:</span>{" "}
                <span className="font-mono break-all">{cert.metadata.sans.join(", ")}</span>
              </div>
            )}
          </div>
        )}

        {listener.last_error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs">
            <div className="flex items-center gap-1 text-destructive font-semibold">
              <AlertTriangle className="h-3 w-3" /> Listener error
            </div>
            <div className="mt-1 break-all">{listener.last_error}</div>
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={onSave} data-testid="btn-save-tls">
            Save TLS settings
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={runTest}
            disabled={testing || !ready}
            data-testid="btn-test-tls"
          >
            {testing ? "Testing…" : "Test handshake"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmailCredentialsCard() {
  const [state, setState] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [fromAddr, setFromAddr] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/platform/email/credentials");
      setState(r.data);
      setFromAddr(r.data?.db_from_address || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load credentials");
    }
  }, []);
  useEffect(() => {
    reload();
  }, [reload]);

  const save = async ({ self_test = false } = {}) => {
    setBusy(true);
    try {
      const body = {};
      // Only send api_key if the user typed something (otherwise leave it
      // unchanged on the server). Empty string ⇒ explicit clear.
      if (apiKey !== "") {
        body.api_key = apiKey;
      }
      // Always send the from-address if it differs.
      if ((state?.db_from_address || "") !== fromAddr) {
        body.from_address = fromAddr;
      }
      if (Object.keys(body).length === 0 && !self_test) {
        toast.info("Nothing to save");
        setBusy(false);
        return;
      }
      if (self_test) body.self_test = true;
      const r = await api.put("/platform/email/credentials", body);
      setState(r.data);
      setApiKey("");
      if (r.data?.self_test) {
        if (r.data.self_test.ok) {
          toast.success(`Self-test sent (id ${r.data.self_test.id || "?"})`);
        } else {
          toast.error(`Self-test failed: ${r.data.self_test.error}`);
        }
      } else {
        toast.success("Email credentials saved (no restart needed)");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const clearKey = async () => {
    if (!confirm("Clear the saved Resend API key? The system will fall back to the env-var value if any.")) return;
    setBusy(true);
    try {
      const r = await api.put("/platform/email/credentials", { api_key: "" });
      setState(r.data);
      setApiKey("");
      toast.success("Saved key cleared. Falling back to env value.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  const cfg = state?.configured;
  const src = state?.source; // "database" | "env" | null
  const tail = state?.key_tail;

  return (
    <Card className="max-w-2xl" data-testid="card-email-credentials">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2 mt-0.5">
            <Send className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-base font-semibold">Email service (Resend)</h3>
              <div className="flex items-center gap-1">
                {cfg ? (
                  <Badge variant="outline" className="gap-1 border-green-500/40 text-green-600">
                    <CheckCircle2 className="h-3 w-3" /> Configured
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 border-destructive/40 text-destructive">
                    <AlertTriangle className="h-3 w-3" /> Not configured
                  </Badge>
                )}
                {src === "database" && (
                  <Badge variant="outline" className="gap-1 text-xs">
                    <Database className="h-3 w-3" /> DB
                  </Badge>
                )}
                {src === "env" && (
                  <Badge variant="outline" className="gap-1 text-xs">
                    <Server className="h-3 w-3" /> env
                  </Badge>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-prose">
              Credentials for transactional email delivery. Saving here
              survives upgrades (stored in MongoDB), takes effect within ~5
              seconds without a backend restart, and overrides anything set
              in <code className="font-mono">backend/.env</code>. Get a key
              at{" "}
              <a
                className="text-primary hover:underline"
                href="https://resend.com/api-keys"
                target="_blank"
                rel="noreferrer"
              >
                resend.com/api-keys
              </a>
              ; verify a sending domain at{" "}
              <a
                className="text-primary hover:underline"
                href="https://resend.com/domains"
                target="_blank"
                rel="noreferrer"
              >
                resend.com/domains
              </a>{" "}
              for production deliverability.
            </p>
          </div>
        </div>

        {tail && (
          <div className="text-xs text-muted-foreground" data-testid="email-key-tail">
            Active key ends in{" "}
            <span className="font-mono text-foreground">…{tail}</span>{" "}
            <span className="text-muted-foreground">
              (sourced from {src === "database" ? "database" : "environment variable"})
            </span>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs flex items-center justify-between">
              <span>API key</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setShowKey((v) => !v)}
                title={showKey ? "Hide" : "Reveal what you typed"}
                tabIndex={-1}
              >
                {showKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              </button>
            </Label>
            <Input
              type={showKey ? "text" : "password"}
              autoComplete="new-password"
              spellCheck={false}
              placeholder={
                cfg
                  ? `Leave blank to keep current key (…${tail || "????"})`
                  : "re_xxxxxxxxxxxxxxxxxxxx"
              }
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="email-api-key-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Keys start with <span className="font-mono">re_</span>. We
              only ever show you the last 4 characters of the saved key —
              never the full value.
            </p>
          </div>
          <div>
            <Label className="text-xs">From address</Label>
            <Input
              type="text"
              placeholder={
                state?.env_from_address ||
                "Elswitch <noreply@yourdomain.com>"
              }
              value={fromAddr}
              onChange={(e) => setFromAddr(e.target.value)}
              data-testid="email-from-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Use a verified-domain address for production. Default
              <span className="font-mono"> onboarding@resend.dev</span> is
              rate-limited and often flagged as spam.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save()} disabled={busy} data-testid="email-save">
            Save credentials
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => save({ self_test: true })}
            disabled={busy}
            data-testid="email-save-and-test"
          >
            Save & send self-test
          </Button>
          {src === "database" && (
            <Button
              type="button"
              variant="ghost"
              onClick={clearKey}
              disabled={busy}
              data-testid="email-clear"
              className="text-destructive hover:text-destructive"
            >
              Clear saved key
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PlatformSettings() {
  /* -------- platform-wide settings card -------- */
  const [s, setS] = useState({ self_signup_enabled: true, default_plan: "starter" });
  useEffect(() => {
    api.get("/platform/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    await api.put("/platform/settings", s);
    toast.success("Settings saved");
  };

  /* -------- listen-ports card -------- */
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ port: "", label: "" });
  // Phase N+ — also pull the TLS listener state so the listen-ports table
  // can show a read-only 5061/TLS row alongside the plain UDP/TCP ports.
  const [tlsStatus, setTlsStatus] = useState(null);

  const loadPorts = useCallback(async () => {
    try {
      const [rp, rt] = await Promise.all([
        api.get("/platform/listen-ports"),
        api.get("/platform/tls/status").catch(() => ({ data: null })),
      ]);
      setPorts(Array.isArray(rp.data) ? rp.data : []);
      setTlsStatus(rt.data);
    } catch {
      /* silent — likely perms or transient */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPorts();
    const t = setInterval(loadPorts, 5000);   // polls every 5s to match engine reconciler
    return () => clearInterval(t);
  }, [loadPorts]);

  const openAdd = () => {
    setDraft({ port: "", label: "" });
    setOpen(true);
  };

  const submitAdd = async () => {
    const portNum = parseInt(draft.port, 10);
    if (!portNum || portNum < 1 || portNum > 65535) {
      toast.error("Enter a valid port (1–65535)");
      return;
    }
    try {
      await api.post("/platform/listen-ports", {
        port: portNum,
        label: draft.label || null,
      });
      toast.success(`Port ${portNum} queued for binding`);
      setOpen(false);
      loadPorts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add port");
    }
  };

  const toggleEnabled = async (row, next) => {
    try {
      await api.patch(`/platform/listen-ports/${row.id}`, { enabled: next });
      toast.success(next ? `Port ${row.port} enabling…` : `Port ${row.port} disabling…`);
      loadPorts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const removePort = async (row) => {
    if (!window.confirm(`Remove listen port ${row.port}? Existing calls are unaffected.`)) return;
    try {
      await api.delete(`/platform/listen-ports/${row.id}`);
      toast.success(`Port ${row.port} removed`);
      loadPorts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="page-platform-settings" className="space-y-6">
      <PageHeader title="Platform Settings" icon={Settings} />

      {/* ------- self-signup / plan card ------- */}
      <Card className="max-w-2xl">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label>Self-signup enabled</Label>
              <p className="text-xs text-muted-foreground mt-1">
                When on, anyone can register their ITSP at <code>/signup</code>. New orgs land
                in <b>pending</b> state and will have all SIP traffic rejected by the engine until
                you approve them in the Tenants list.
              </p>
            </div>
            <Switch
              checked={!!s.self_signup_enabled}
              onCheckedChange={(v) => setS({ ...s, self_signup_enabled: v })}
              data-testid="toggle-self-signup"
            />
          </div>
          <div>
            <Label>Default plan for self-signed tenants</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm mt-1"
              value={s.default_plan}
              onChange={(e) => setS({ ...s, default_plan: e.target.value })}
            >
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="scale">Scale</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <Button onClick={save} data-testid="platform-settings-save">
            Save settings
          </Button>
        </CardContent>
      </Card>

      {/* ------- engine modules card ------- */}
      <EngineModulesCard
        modules={s.engine_modules || {}}
        onChange={(em) => setS({ ...s, engine_modules: { ...s.engine_modules, ...em } })}
        onSave={save}
      />

      {/* ------- trial defaults card (Phase 6r) ------- */}
      <Card className="max-w-2xl" data-testid="card-trial-defaults">
        <CardContent className="p-5 space-y-4">
          <div>
            <Label>Free-plan trial duration (days)</Label>
            <p className="text-xs text-muted-foreground mt-1">
              When a new account signs up on the <b>Free</b> plan, the engine will reject calls
              after this many days. <b>0</b> disables the timer (legacy "free forever" behaviour).
              Existing tenants are not retroactively expired — the boot migration only backfills
              tenants that have <i>no</i> expiry date set.
              <br />
              Tenants can be exempted individually from the customer editor (Beta tester / comp
              account flow).
            </p>
            <Input
              type="number"
              min={0}
              max={3650}
              value={s.trial_days ?? 30}
              onChange={(e) =>
                setS({ ...s, trial_days: Math.max(0, Number(e.target.value || 0)) })
              }
              className="mt-2 w-40"
              data-testid="trial-days-input"
            />
          </div>
          <Button onClick={save} data-testid="trial-defaults-save">
            Save settings
          </Button>
        </CardContent>
      </Card>

      {/* ------- SIP / NAT engine tuning card (Phase 6y) ------- */}
      <Card className="max-w-2xl" data-testid="card-engine-tuning">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">SIP / NAT engine tuning</h3>
            <span
              className="inline-flex items-center gap-1 rounded border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--warning))]"
              title="Engine restart required"
            >
              <RefreshCw className="h-3 w-3" />
              restart required
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            These knobs are read by the SIP engine at startup. <b>Saving here updates the database
            but does not apply until the engine is restarted</b>. Use{" "}
            <b>Platform → System → Restart engine</b> from the UI, or via SSH:&nbsp;
            <code className="text-[11px] bg-muted/60 px-1 rounded">
              sudo systemctl restart elswitch-engine
            </code>
            . The corresponding env var (e.g.&nbsp;
            <code className="text-[11px] bg-muted/60 px-1 rounded">
              ELSWITCH_MAX_REGISTER_EXPIRES
            </code>
            ) still wins when set in&nbsp;
            <code className="text-[11px] bg-muted/60 px-1 rounded">
              /opt/elswitch/shared/env/engine.env
            </code>
            &nbsp;(back-compat for existing deployments).
          </p>
          <div>
            <Label>Customer REGISTER expiry cap (seconds)</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Caps whatever expiry the customer softphone (Voiper / Bria / Linphone / Zoiper)
              proposes in REGISTER. <b>60</b> is the cellular-NAT sweet spot; <b>30</b> for
              very strict carriers. <b>0</b> disables the cap entirely (LAN-only deployments).
              Without this cap, mobile softphones blackhole inbound calls within ~1 min of the
              last REGISTER.
            </p>
            <Input
              type="number"
              min={0}
              max={3600}
              value={s.max_register_expires ?? 60}
              onChange={(e) =>
                setS({
                  ...s,
                  max_register_expires: Math.max(0, Math.min(3600, Number(e.target.value || 0))),
                })
              }
              className="mt-2 w-40"
              data-testid="max-register-expires-input"
            />
          </div>
          <Button onClick={save} data-testid="engine-tuning-save">
            Save (restart engine to apply)
          </Button>
        </CardContent>
      </Card>

      {/* ------- max-call-duration card (Phase N) ------- */}
      <MaxCallDurationCard
        value={s.max_call_duration ?? 21600}
        onChange={(v) => setS({ ...s, max_call_duration: v })}
        onSave={save}
      />

      {/* ------- TLS listener card (Phase 6a) ------- */}
      <TlsListenerCard
        s={s}
        onChange={(patch) => setS({ ...s, ...patch })}
        onSave={save}
      />

      {/* ------- SIP public host card (Phase 6d) ------- */}
      <Card className="max-w-2xl" data-testid="card-sip-public-host">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-primary/10 p-2 mt-0.5">
              <Network className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold">SIP public host</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-prose">
                The address the engine writes into outbound{" "}
                <span className="font-mono">Via</span> /{" "}
                <span className="font-mono">From</span> /{" "}
                <span className="font-mono">Contact</span> headers when
                placing calls to internet trunks (e.g. sip.us). Required
                whenever the engine listens on{" "}
                <span className="font-mono">0.0.0.0</span> behind a public
                IP/FQDN — without it, carriers route their responses to{" "}
                <span className="font-mono">0.0.0.0</span> and the call
                fails silently. Falls back to the TLS cert domain when
                empty. Set the <span className="font-mono">ELSWITCH_PUBLIC_HOST</span>{" "}
                env var on the host to lock this value at the OS level.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Public host (FQDN or public IP)</Label>
              <Input
                type="text"
                value={s.sip_public_host || ""}
                onChange={(e) =>
                  setS({ ...s, sip_public_host: e.target.value.trim() })
                }
                placeholder={
                  s.tls_cert_domain
                    ? `e.g. ${s.tls_cert_domain}`
                    : "e.g. sip.example.com or 203.0.113.42"
                }
                data-testid="input-sip-public-host"
              />
              {!s.sip_public_host && s.tls_cert_domain && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Currently inheriting{" "}
                  <span className="font-mono">{s.tls_cert_domain}</span>{" "}
                  from the TLS cert domain. Set this explicitly to override.
                </p>
              )}
              {!s.sip_public_host && !s.tls_cert_domain && (
                <p className="text-[10px] text-destructive mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Not configured —
                  outbound calls will fail silently if the engine binds on
                  0.0.0.0.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Public port (optional)</Label>
              <NumberInput
                min={0}
                max={65535}
                fallback={0}
                value={s.sip_public_port ?? 0}
                onChange={(n) => setS({ ...s, sip_public_port: n })}
                data-testid="input-sip-public-port"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                0 means use the SIP listener bind port.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={save} data-testid="btn-save-sip-public-host">
              Save SIP public host
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ------- security policy card (Phase G) ------- */}
      <Card className="max-w-2xl" data-testid="card-security-policy">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-primary/10 p-2 mt-0.5">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Security policy</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Per-IP rate limits applied to authentication endpoints.
                Tighten these the moment you go public.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Login attempts / minute / IP</Label>
              <NumberInput
                min={1}
                max={600}
                fallback={10}
                value={s.security?.login_rate_per_min ?? 10}
                onChange={(n) =>
                  setS({
                    ...s,
                    security: {
                      ...(s.security || {}),
                      login_rate_per_min: n,
                    },
                  })
                }
                data-testid="security-login-rate"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Default 10. Lower → safer; higher → fewer 429s on shared NATs.
              </p>
            </div>
            <div>
              <Label className="text-xs">Signups / hour / IP</Label>
              <NumberInput
                min={1}
                max={100}
                fallback={3}
                value={s.security?.signup_rate_per_hour ?? 3}
                onChange={(n) =>
                  setS({
                    ...s,
                    security: {
                      ...(s.security || {}),
                      signup_rate_per_hour: n,
                    },
                  })
                }
                data-testid="security-signup-rate"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Default 3. Combined with hCaptcha for bot resistance.
              </p>
            </div>
          </div>
          <Button onClick={save} data-testid="security-save">
            Save security policy
          </Button>
        </CardContent>
      </Card>

      {/* ------- email service credentials card (Phase N+) ------- */}
      <EmailCredentialsCard />

      {/* ------- notification email card (Phase H) ------- */}
      <Card className="max-w-2xl" data-testid="card-notify">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-primary/10 p-2 mt-0.5">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Notification email</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Where security alerts (admin password changes, 2FA disables,
                tenant suspends) and billing-mail BCCs get delivered. Multiple
                addresses? Comma-separate them.
              </p>
            </div>
          </div>
          <div>
            <Label className="text-xs">Email address(es)</Label>
            <Input
              type="text"
              placeholder="ops@yourdomain.com, alerts@yourdomain.com"
              value={s.admin_notify_email || ""}
              onChange={(e) => setS({ ...s, admin_notify_email: e.target.value })}
              data-testid="notify-email-input"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} data-testid="notify-save">Save</Button>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                try {
                  await api.put("/platform/settings", s);
                  const r = await api.post("/platform/security/test-alert");
                  const to = Array.isArray(r.data?.sent_to)
                    ? r.data.sent_to.join(", ")
                    : r.data?.sent_to;
                  toast.success(
                    `Sent to ${to}${r.data?.resend_id ? ` (id ${r.data.resend_id})` : ""}`,
                    { duration: 8000 },
                  );
                  if (r.data?.note) {
                    // Surface the deliverability hint as a separate info toast.
                    toast.info(r.data.note, { duration: 12000 });
                  }
                } catch (e) {
                  toast.error(
                    e?.response?.data?.detail || "Could not send",
                    { duration: 12000 },
                  );
                }
              }}
              data-testid="notify-test"
              disabled={!s.admin_notify_email}
            >
              Send test alert
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ------- 2FA card (Phase H) ------- */}
      <div className="max-w-2xl">
        <TwoFactorCard />
      </div>

      {/* ------- Legal documents editor (Phase L) ------- */}
      <LegalEditorCard />

      {/* ------- Phase 4m — Factory Reset danger zone ------- */}
      <FactoryResetCard />

      {/* ------- listen ports card ------- */}
      <Card data-testid="card-listen-ports">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-primary/10 p-2 mt-0.5">
                <Network className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-base font-semibold">SIP listen ports</h3>
                <p className="text-xs text-muted-foreground mt-1 max-w-prose">
                  The engine listens for SIP/UDP on <b>5060</b> by default. If a customer needs to
                  reach you on a different port, add it here — the engine will auto-bind it in
                  under 5 seconds, <b>no restart required</b>. Responses always leave the same
                  port they arrived on (symmetric SIP).
                </p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Shield className="h-3 w-3" /> Firewall rules, auto-bans, and repeat-offender
                  detection apply to every listen port automatically.
                </p>
              </div>
            </div>
            <Button onClick={openAdd} data-testid="btn-listen-port-add" className="gap-1">
              <Plus className="h-4 w-4" /> Add port
            </Button>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-6">Loading…</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Port</TableHead>
                    <TableHead className="w-24">Transport</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                    <TableHead className="w-28 text-right">Pkts in</TableHead>
                    <TableHead className="w-28 text-right">Pkts out</TableHead>
                    <TableHead className="w-28">Enabled</TableHead>
                    <TableHead className="w-20 text-right">Remove</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ports.length === 0 && !tlsStatus?.config?.enabled && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                        No listen ports configured.
                      </TableCell>
                    </TableRow>
                  )}
                  {ports.map((row) => (
                    <TableRow key={row.id} data-testid={`lp-row-${row.port}`}>
                      <TableCell className="font-mono font-semibold">
                        {row.port}
                        {row.is_primary && (
                          <Badge variant="outline" className="ml-2 text-[10px]">primary</Badge>
                        )}
                      </TableCell>
                      <TableCell className="uppercase text-muted-foreground">
                        {row.transport}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{row.label || "—"}</div>
                        {row.last_error && (
                          <div className="text-xs text-destructive mt-0.5">
                            {row.last_error}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={row.status} enabled={row.enabled} />
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {row.live_packets_in ?? 0}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {row.live_packets_out ?? 0}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!row.enabled}
                          disabled={row.is_primary}
                          onCheckedChange={(v) => toggleEnabled(row, v)}
                          data-testid={`lp-toggle-${row.port}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={row.is_primary}
                          onClick={() => removePort(row)}
                          data-testid={`lp-delete-${row.port}`}
                          title={row.is_primary ? "Primary port cannot be removed" : "Remove"}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Phase N+ — read-only 5061/TLS row (managed in the
                      dedicated SIP-TLS listener card). Shown only when
                      the operator has opted in to TLS so we don't clutter
                      the table for deployments that don't use SIPS. */}
                  {tlsStatus?.config?.enabled && (
                    <TableRow
                      data-testid="lp-row-tls"
                      className="bg-muted/30"
                    >
                      <TableCell className="font-mono font-semibold flex items-center gap-1.5">
                        <Lock className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                        {tlsStatus.config.port || 5061}
                      </TableCell>
                      <TableCell className="uppercase text-[hsl(var(--accent))] font-semibold">
                        TLS
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          SIP over TLS (SIPS) · RFC 3261 §26.2
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Managed in the <b>SIP-TLS listener</b> card above
                          {tlsStatus.cert?.domain && (
                            <> · cert for <span className="font-mono">{tlsStatus.cert.domain}</span></>
                          )}
                          {tlsStatus.listener?.last_error && (
                            <div className="text-destructive mt-0.5 break-all">
                              {tlsStatus.listener.last_error}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {tlsStatus.listener?.reported_active ? (
                          <Badge className="gap-1" data-testid="lp-tls-status-active">
                            <CheckCircle2 className="h-3 w-3" /> bound
                          </Badge>
                        ) : tlsStatus.ready_to_enable ? (
                          <Badge variant="outline" className="gap-1">
                            <Activity className="h-3 w-3 animate-pulse" /> starting
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 border-destructive/40 text-destructive">
                            <AlertTriangle className="h-3 w-3" /> cert
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">
                        —
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">
                        —
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!tlsStatus.config.enabled}
                          disabled
                          data-testid="lp-tls-toggle"
                          title="Toggle in the SIP-TLS listener card"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled
                          title="Managed in the SIP-TLS listener card"
                        >
                          <Lock className="h-4 w-4 opacity-60" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ------- add-port dialog ------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-add-listen-port">
          <DialogHeader>
            <DialogTitle>Add SIP listen port</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Port (1024–65535)</Label>
              <Input
                type="number"
                min={1024}
                max={65535}
                value={draft.port}
                onChange={(e) => setDraft({ ...draft, port: e.target.value })}
                placeholder="e.g. 5080"
                data-testid="input-listen-port-number"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Reserved ports below 1024 are not allowed (except the built-in primary 5060).
              </p>
            </div>
            <div>
              <Label>Label (optional)</Label>
              <Input
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Customer Acme PBX"
                data-testid="input-listen-port-label"
                maxLength={60}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submitAdd} data-testid="btn-listen-port-submit">
              Add &amp; bind
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
